<script lang="ts">
	import ChevronLeftIcon from "@lucide/svelte/icons/chevron-left";
	import ChevronRightIcon from "@lucide/svelte/icons/chevron-right";
	import XIcon from "@lucide/svelte/icons/x";
	import { cloudinaryFetch } from "#lib/shared/cloudinary.js";
	import Skeleton from "#lib/web-ui/ui/skeleton/skeleton.svelte";
	import { onCleanup } from "runed";

	type ListingImage = {
		url: string;
		lightboxUrl?: string;
	};

	const SWIPE_THRESHOLD = 50;

	let { media }: { media: ListingImage[] | null | undefined } = $props();
	let images = $derived(media ?? []);
	let currentImageIndex = $state(0);
	let isTransitioning = $state(false);
	let isLightboxOpen = $state(false);
	let imageAspectRatios = $state<Record<number, number>>({});
	let transitionTimer: ReturnType<typeof setTimeout> | undefined;
	let didSwipe = false;
	let lightboxEl = $state<HTMLDivElement | undefined>();
	let previousFocus: Element | null = null;

	$effect(() => {
		currentImageIndex = 0;
		imageAspectRatios = {};

		if (!images.length) return;

		const imagesToPreload = images;
		const preloadTimeout = setTimeout(() => {
			imagesToPreload.forEach((image, index) => {
				const preloadedImage = new Image();
				preloadedImage.onload = () => {
					if (imagesToPreload !== images || !preloadedImage.height) return;
					imageAspectRatios[index] = preloadedImage.width / preloadedImage.height;
				};
				preloadedImage.src = cloudinaryFetch(image.url, { width: 100 });
			});
		}, 100);

		return () => clearTimeout(preloadTimeout);
	});

	onCleanup(() => {
		if (transitionTimer) clearTimeout(transitionTimer);
	});

	function setImage(index: number) {
		if (!images.length || isTransitioning || index === currentImageIndex) return;

		currentImageIndex = (index + images.length) % images.length;
		isTransitioning = true;
		if (transitionTimer) clearTimeout(transitionTimer);
		transitionTimer = setTimeout(() => {
			isTransitioning = false;
			transitionTimer = undefined;
		}, 300);
	}

	function nextImage() {
		if (images.length > 1) setImage(currentImageIndex + 1);
	}

	function prevImage() {
		if (images.length > 1) setImage(currentImageIndex - 1);
	}

	function openLightbox() {
		if (didSwipe) {
			didSwipe = false;
			return;
		}
		if (images.length) isLightboxOpen = true;
	}

	function closeLightbox() {
		isLightboxOpen = false;
	}

	$effect(() => {
		if (isLightboxOpen) {
			document.body.classList.add("overflow-hidden");
			previousFocus = document.activeElement;
			requestAnimationFrame(() => lightboxEl?.focus());

			return () => {
				document.body.classList.remove("overflow-hidden");
				if (previousFocus instanceof HTMLElement) previousFocus.focus();
				previousFocus = null;
			};
		}
	});

	function handleKeydown(event: KeyboardEvent) {
		if (event.defaultPrevented) return;

		if (event.key === "Escape" && isLightboxOpen) {
			event.preventDefault();
			closeLightbox();
			return;
		}

		if (event.key === "Tab" && isLightboxOpen && lightboxEl) {
			event.preventDefault();
			const focusable = Array.from(
				lightboxEl.querySelectorAll<HTMLElement>("button, [tabindex]:not([tabindex='-1'])")
			);
			if (!focusable.length) return;
			const idx = focusable.indexOf(document.activeElement as HTMLElement);
			const next = event.shiftKey
				? idx <= 0
					? focusable.length - 1
					: idx - 1
				: idx >= focusable.length - 1
					? 0
					: idx + 1;
			focusable[next].focus();
			return;
		}

		if (!images.length) return;
		if (event.key === "ArrowLeft") {
			event.preventDefault();
			prevImage();
		} else if (event.key === "ArrowRight") {
			event.preventDefault();
			nextImage();
		}
	}

	function swipeable(node: HTMLElement) {
		let startX: number | null = null;
		let currentX: number | null = null;
		const pointerIds = new Set<number>();

		function onDown(e: PointerEvent) {
			if (e.pointerType === "mouse" && e.button !== 0) return;
			pointerIds.add(e.pointerId);
			node.setPointerCapture(e.pointerId);
			if (pointerIds.size > 1) {
				startX = null;
				currentX = null;
				didSwipe = false;
				return;
			}
			startX = e.clientX;
			currentX = e.clientX;
			didSwipe = false;
		}

		function onMove(e: PointerEvent) {
			if (startX !== null) currentX = e.clientX;
		}

		function onUp(e: PointerEvent) {
			pointerIds.delete(e.pointerId);
			try {
				node.releasePointerCapture(e.pointerId);
			} catch {
				/* already released */
			}
			if (pointerIds.size > 0 || startX === null || currentX === null) {
				startX = null;
				currentX = null;
				return;
			}
			const delta = currentX - startX;
			startX = null;
			currentX = null;
			if (Math.abs(delta) < SWIPE_THRESHOLD) return;
			didSwipe = true;
			if (delta < 0) nextImage();
			else prevImage();
		}

		function onCancel(e: PointerEvent) {
			pointerIds.delete(e.pointerId);
			try {
				node.releasePointerCapture(e.pointerId);
			} catch {
				/* already released */
			}
			startX = null;
			currentX = null;
		}

		node.addEventListener("pointerdown", onDown);
		node.addEventListener("pointermove", onMove);
		node.addEventListener("pointerup", onUp);
		node.addEventListener("pointercancel", onCancel);

		return () => {
			node.removeEventListener("pointerdown", onDown);
			node.removeEventListener("pointermove", onMove);
			node.removeEventListener("pointerup", onUp);
			node.removeEventListener("pointercancel", onCancel);
		};
	}

	function handleLightboxBackdropClick(event: MouseEvent) {
		if (event.target === event.currentTarget) closeLightbox();
	}
</script>

<svelte:window onkeydown={images.length || isLightboxOpen ? handleKeydown : undefined} />

<section id="image-gallery" class="mx-auto mb-4 w-full lg:max-w-[1400px] lg:px-8 lg:pt-6">
	{#if !images.length}
		<div
			class="aspect-[3/2] w-full overflow-hidden bg-black lg:aspect-auto lg:h-[600px] lg:rounded-t"
		>
			<Skeleton class="h-full w-full rounded-none" />
		</div>
		<div class="flex w-full gap-2 overflow-x-auto bg-black p-2 lg:rounded-b">
			{#each Array(4) as _, i (i)}
				<Skeleton class="h-16 w-24 shrink-0 rounded" />
			{/each}
		</div>
	{:else}
		<div
			class={`relative aspect-[3/2] w-full overflow-hidden bg-black lg:aspect-auto lg:h-[600px] ${images.length > 1 ? "lg:rounded-t" : "lg:rounded"}`}
		>
			<button
				type="button"
				class="flex h-full w-full cursor-zoom-in touch-pan-y items-center justify-center focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-hidden"
				onclick={openLightbox}
				{@attach swipeable}
				aria-label={`Open image ${currentImageIndex + 1} in fullscreen`}
			>
				<img
					src={cloudinaryFetch(images[currentImageIndex].url, { width: 1200 })}
					alt={`Property image ${currentImageIndex + 1}`}
					draggable="false"
					class={`max-h-full max-w-full object-contain transition-opacity duration-300 ${
						isTransitioning ? "opacity-80" : ""
					} ${
						imageAspectRatios[currentImageIndex] &&
						imageAspectRatios[currentImageIndex] < 1
							? "h-full w-auto"
							: "h-auto w-full"
					}`}
					width="1200"
					height="800"
				/>
			</button>

			{#if images.length > 1}
				<button
					type="button"
					class="absolute top-1/2 left-4 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white transition-opacity hover:bg-black/70"
					onclick={prevImage}
					aria-label="Previous image"
				>
					<ChevronLeftIcon class="h-6 w-6" />
				</button>
				<button
					type="button"
					class="absolute top-1/2 right-4 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white transition-opacity hover:bg-black/70"
					onclick={nextImage}
					aria-label="Next image"
				>
					<ChevronRightIcon class="h-6 w-6" />
				</button>
			{/if}

			<div class="absolute right-4 bottom-4 rounded bg-black/50 px-3 py-1 text-sm text-white">
				{currentImageIndex + 1} / {images.length}
			</div>
		</div>

		{#if images.length > 1}
			<div class="flex w-full gap-2 overflow-x-auto bg-black p-2 lg:rounded-b">
				{#each images as image, index (image.url)}
					<button
						type="button"
						class={`h-16 w-24 shrink-0 overflow-hidden rounded transition-opacity duration-300 ${
							currentImageIndex === index ? "ring-2 ring-white" : ""
						}`}
						onclick={() => setImage(index)}
						aria-label={`View image ${index + 1}`}
						aria-pressed={currentImageIndex === index}
					>
						<img
							src={cloudinaryFetch(image.url, {
								width: 120,
								height: 80,
								crop: "fill"
							})}
							alt={`Thumbnail ${index + 1}`}
							draggable="false"
							class="h-full w-full object-cover"
							width="120"
							height="80"
							loading="lazy"
						/>
					</button>
				{/each}
			</div>
		{/if}
	{/if}
</section>

{#if isLightboxOpen}
	<div
		bind:this={lightboxEl}
		class="fixed inset-0 z-[60] flex items-center justify-center bg-black p-4"
		role="dialog"
		aria-modal="true"
		aria-label="Property image gallery"
		tabindex="-1"
		onclick={handleLightboxBackdropClick}
		onkeydown={handleKeydown}
	>
		<div
			class="relative flex h-full w-full touch-pinch-zoom items-center justify-center"
			{@attach swipeable}
		>
			<img
				src={cloudinaryFetch(
					images[currentImageIndex].lightboxUrl ?? images[currentImageIndex].url,
					{ width: 2000 }
				)}
				alt={`Property image ${currentImageIndex + 1}`}
				draggable="false"
				class="max-h-[calc(100vh-2rem)] max-w-full object-contain"
				width="2000"
				height="1333"
			/>
		</div>

		<button
			type="button"
			class="absolute top-4 right-4 z-10 rounded-full bg-black/60 p-2 text-white hover:bg-black/80"
			onclick={closeLightbox}
			aria-label="Close image gallery"
		>
			<XIcon class="h-6 w-6" />
		</button>

		{#if images.length > 1}
			<button
				type="button"
				class="absolute top-1/2 left-4 z-10 -translate-y-1/2 rounded-full bg-black/60 p-3 text-white hover:bg-black/80"
				onclick={prevImage}
				aria-label="Previous image"
			>
				<ChevronLeftIcon class="h-7 w-7" />
			</button>
			<button
				type="button"
				class="absolute top-1/2 right-4 z-10 -translate-y-1/2 rounded-full bg-black/60 p-3 text-white hover:bg-black/80"
				onclick={nextImage}
				aria-label="Next image"
			>
				<ChevronRightIcon class="h-7 w-7" />
			</button>
		{/if}

		<div
			class="absolute top-4 left-1/2 -translate-x-1/2 rounded bg-black/60 px-3 py-1 text-sm text-white"
		>
			{currentImageIndex + 1} / {images.length}
		</div>
	</div>
{/if}
